import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Empty, Table, Td, Badge } from "../components/UI";
import { FLabel, FInput, FSelect, FTextarea } from "./engagement/FormControls";
import { DateField } from "./engagement/DateField";
import { SlidePanel, ConfirmDialog } from "../components/SlidePanel";
import { useClients, ClientSelect } from "./clinical/shared";
import { usePagination, PaginationBar } from "../components/Pagination";
import { ClipboardCheck, TrendingUp, AlertTriangle, Archive, Eye, Plus, ShieldCheck, FileEdit, PenLine, Info, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { SignaturePromptModal } from "../components/SignaturePromptModal";

const RISK_TONE = {
  none: "bg-[#E8F5E9] text-[#2E7D32]", low: "bg-[#FFF8E1] text-[#8D6E00]",
  moderate: "bg-[#FFF3E0] text-[#B84A00]", high: "bg-[#FFEBEE] text-[#C62828]",
};

const PROGRESS_OPTIONS = [
  ["progress_continue", "Progress Made: Continue Intervention"],
  ["progress_modify", "Progress Made: Modify Goal/Objective/Intervention"],
  ["no_progress_continue", "No Progress Made: Continue Intervention"],
  ["no_progress_modify", "No Progress Made: Modify Goal/Objective/Intervention"],
  ["progress_undetermined", "Progress Undetermined"],
  ["goal_met", "Goal Met: Stop Intervention"],
];

function ScoreBadge({ row }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${RISK_TONE[row.risk_level] || RISK_TONE.low}`}>
      {row.risk_level === "high" && <AlertTriangle size={11} />}{row.score}/{row.max_score} · {row.severity}
    </span>
  );
}

function SidebarNav({ sections, active, setActive, completed }) {
  return (
    <div data-testid="om-admin-sidebar" className="w-56 shrink-0 space-y-1 border-r border-[#EFEBE0] pr-3">
      {sections.map((s) => (
        <button key={s.key} type="button" data-testid={`om-side-${s.key}`}
          onClick={() => setActive(s.key)}
          className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${active === s.key ? "bg-[#4E342E] text-white" : "text-[#6D4C41] hover:bg-[#F5F2EB]"}`}>
          <span className="flex items-center gap-2 text-left">
            <s.icon size={12} className="shrink-0" />
            <span className="truncate">{s.label}</span>
          </span>
          {completed[s.key] && <span className={`text-[10px] font-bold ${active === s.key ? "text-white/80" : "text-[#2E7D32]"}`}>✓</span>}
          {s.required && !completed[s.key] && <span className={`text-[10px] font-bold ${active === s.key ? "text-[#FFCDD2]" : "text-[#C62828]"}`}>*</span>}
        </button>
      ))}
    </div>
  );
}

function SessionInfoSection({ session, setSession }) {
  const f = (k) => (e) => setSession((s) => ({ ...s, [k]: e.target.value }));
  const services = session.services || [];
  const setSvc = (i, k, v) => setSession((s) => ({ ...s, services: services.map((r, ix) => ix === i ? { ...r, [k]: v } : r) }));
  return (
    <div className="space-y-3" data-testid="om-session-info">
      <div className="grid grid-cols-3 gap-3">
        <div><FLabel>Session #</FLabel><FInput data-testid="om-session-number" value={session.session_number || ""} onChange={f("session_number")} /></div>
        <div><FLabel>Start time</FLabel><FInput data-testid="om-session-start" type="time" value={session.start_time || ""} onChange={f("start_time")} /></div>
        <div><FLabel>End time</FLabel><FInput data-testid="om-session-end" type="time" value={session.end_time || ""} onChange={f("end_time")} /></div>
        <div><FLabel>Provider</FLabel><FInput data-testid="om-session-provider" value={session.provider || ""} onChange={f("provider")} /></div>
        <div><FLabel>Program</FLabel><FInput data-testid="om-session-program" value={session.program || ""} onChange={f("program")} /></div>
        <div><FLabel>Visit type</FLabel>
          <FSelect data-testid="om-session-visit-type" value={session.visit_type || ""} onChange={f("visit_type")}>
            {["", "Office Visit", "Telehealth", "Home Visit", "Community", "Crisis"].map((v) => <option key={v}>{v}</option>)}
          </FSelect></div>
        <div className="col-span-2"><FLabel>Location</FLabel><FInput data-testid="om-session-location" value={session.location || ""} onChange={f("location")} /></div>
        <div><FLabel>Other attendees</FLabel><FInput data-testid="om-session-attendees" value={session.attendees || ""} onChange={f("attendees")} /></div>
      </div>
      <div>
        <FLabel>Services provided</FLabel>
        <div className="space-y-1.5">
          {services.map((row, i) => (
            <div key={i} className="grid grid-cols-[110px_1fr_28px] gap-2">
              <FInput data-testid={`om-svc-cpt-${i}`} placeholder="CPT" value={row.cpt || ""} onChange={(e) => setSvc(i, "cpt", e.target.value)} />
              <FInput data-testid={`om-svc-desc-${i}`} placeholder="Service description" value={row.description || ""} onChange={(e) => setSvc(i, "description", e.target.value)} />
              <button type="button" onClick={() => setSession((s) => ({ ...s, services: services.filter((_, ix) => ix !== i) }))} className="text-[#C62828] text-xs font-bold" aria-label="Remove service">Remove</button>
            </div>
          ))}
          <button type="button" data-testid="om-svc-add" onClick={() => setSession((s) => ({ ...s, services: [...services, {}] }))}
            className="text-xs font-semibold text-[#7B6157] hover:underline">+ Add service</button>
        </div>
      </div>
    </div>
  );
}

function InstrumentSection({ inst, answers, setAnswers, notes, setNotes }) {
  if (!inst) return <Empty text="Select an instrument to begin." />;
  return (
    <div className="space-y-3" data-testid="om-instrument-section">
      <p className="rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm text-[#5D4037] italic">{inst.prompt}</p>
      {inst.questions.map((q, i) => {
        const opts = inst.per_question_options?.[String(i)] || inst.options;
        return (
          <div key={i} className="rounded-xl border border-[#EFEBE0] p-4" data-testid={`om-question-${i}`}>
            <div className="mb-2 text-sm font-medium text-[#3E2723]">{i + 1}. {q}</div>
            <div className="flex flex-wrap gap-2">
              {opts.map(([val, label]) => (
                <button key={val} type="button" data-testid={`om-q${i}-opt${val}`}
                  onClick={() => setAnswers((s) => ({ ...s, [i]: val }))}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${answers[i] === val ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      <div><FLabel>Clinical notes (optional)</FLabel><FTextarea data-testid="om-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
    </div>
  );
}

function SrsSection({ srsMeta, srsAnswers, setSrsAnswers, mandatory }) {
  if (!srsMeta) return <Empty text="Loading Suicide Risk Screen…" />;
  return (
    <div className="space-y-3" data-testid="om-srs-section">
      <div className="flex items-start gap-2 rounded-xl border border-[#FFB300] bg-[#FFF8E1] p-3 text-xs text-[#8D6E00]">
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#8D5A00]" />
        <div>
          <div className="font-bold text-[#3E2723]">
            {mandatory ? "Mandatory - Joint Commission NPSG 15.01.01" : "Optional Suicide Risk Screen"}
          </div>
          <div className="mt-0.5 text-[11px] text-[#6D4C41]">C-SSRS Screen. All six items must be answered before this instrument can be saved.</div>
        </div>
      </div>
      <p className="rounded-xl bg-[#F5F2EB] px-4 py-3 text-sm text-[#5D4037] italic">{srsMeta.prompt}</p>
      {srsMeta.questions.map((q, i) => (
        <div key={i} className="rounded-xl border border-[#EFEBE0] p-4" data-testid={`om-srs-q-${i}`}>
          <div className="mb-2 text-sm font-medium text-[#3E2723]">{i + 1}. {q}</div>
          <div className="flex flex-wrap gap-2">
            {srsMeta.options.map(([val, label]) => (
              <button key={val} type="button" data-testid={`om-srs-q${i}-opt${val}`}
                onClick={() => setSrsAnswers((s) => ({ ...s, [i]: val }))}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${srsAnswers[i] === val ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProgramNotificationSection({ prog, setProg }) {
  return (
    <div className="space-y-4" data-testid="om-progress-section">
      <div>
        <FLabel>Progress</FLabel>
        <div className="space-y-1.5">
          {PROGRESS_OPTIONS.map(([v, label]) => (
            <label key={v} className="flex items-start gap-2 text-sm text-[#4E342E]">
              <input type="radio" name="om-progress" data-testid={`om-progress-${v}`}
                checked={prog.progress === v}
                onChange={() => setProg((s) => ({ ...s, progress: v }))} />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <FLabel>Is there a need to modify, amend, or update the treatment plan or recovery plan?</FLabel>
        <div className="flex gap-4 text-sm text-[#4E342E]">
          <label className="flex items-center gap-1"><input type="radio" name="om-mod" data-testid="om-mod-yes" checked={prog.needs_modification === true} onChange={() => setProg((s) => ({ ...s, needs_modification: true }))} /> Yes</label>
          <label className="flex items-center gap-1"><input type="radio" name="om-mod" data-testid="om-mod-no" checked={prog.needs_modification === false} onChange={() => setProg((s) => ({ ...s, needs_modification: false, modifications: "" }))} /> No</label>
        </div>
      </div>
      {prog.needs_modification && (
        <div>
          <FLabel>What modification(s) will be made?</FLabel>
          <FTextarea data-testid="om-modifications" rows={3} value={prog.modifications || ""}
            onChange={(e) => setProg((s) => ({ ...s, modifications: e.target.value }))} />
        </div>
      )}
      <div>
        <FLabel>Additional Information</FLabel>
        <FTextarea data-testid="om-additional-info" rows={3} value={prog.additional_info || ""}
          onChange={(e) => setProg((s) => ({ ...s, additional_info: e.target.value }))} />
      </div>
    </div>
  );
}

function AdministerPanel({ open, onClose, instruments, clients, onSaved, srsMeta, behavioralCategories }) {
  const [active, setActive] = useState("client");
  const [instKey, setInstKey] = useState("");
  const [clientId, setClientId] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [session, setSession] = useState({});
  const [srsAnswers, setSrsAnswers] = useState({});
  const [prog, setProg] = useState({ progress: "", needs_modification: false, modifications: "", additional_info: "" });
  const [busy, setBusy] = useState(false);

  const inst = instruments.find((i) => i.key === instKey);
  const isBehavioral = !!inst && behavioralCategories.includes(inst.category) && !inst.is_risk_screen;

  useEffect(() => {
    if (open) {
      setActive("client"); setInstKey(""); setClientId(""); setAnswers({}); setNotes(""); setSession({});
      setSrsAnswers({}); setProg({ progress: "", needs_modification: false, modifications: "", additional_info: "" });
    }
  }, [open]);

  const sections = useMemo(() => [
    { key: "client", label: "Client & Date", icon: ClipboardCheck, required: true },
    { key: "session", label: "Session Information", icon: PenLine },
    { key: "instrument", label: inst ? inst.name : "Instrument", icon: FileEdit, required: true },
    ...(isBehavioral ? [{ key: "srs", label: "Suicide Risk Screen", icon: ShieldCheck, required: true }] : []),
    { key: "program", label: "Program & Treatment", icon: TrendingUp },
  ], [inst, isBehavioral]);

  const completed = useMemo(() => ({
    client: !!clientId,
    session: !!(session.session_number || session.provider),
    instrument: inst && Object.keys(answers).length === inst.questions.length,
    srs: isBehavioral && Object.keys(srsAnswers).length === (srsMeta?.questions?.length || 6),
    program: !!prog.progress,
  }), [clientId, session, inst, answers, isBehavioral, srsAnswers, srsMeta, prog]);

  const save = async () => {
    if (!clientId) { setActive("client"); return toast.error("Select a client"); }
    if (!inst) { setActive("instrument"); return toast.error("Select an instrument"); }
    if (Object.keys(answers).length !== inst.questions.length) { setActive("instrument"); return toast.error("Answer all instrument items"); }
    if (isBehavioral && Object.keys(srsAnswers).length !== (srsMeta?.questions?.length || 6)) {
      setActive("srs");
      return toast.error("Suicide Risk Screen is mandatory. Answer all six items.");
    }
    setBusy(true);
    try {
      const payload = {
        client_id: clientId, instrument: instKey, date, notes, session,
        answers: inst.questions.map((_, i) => answers[i]),
        srs: isBehavioral ? { answers: srsMeta.questions.map((_, i) => srsAnswers[i]) } : undefined,
        program_notification: prog,
      };
      const res = await api.post("/outcome-measures", payload);
      toast.success(`${inst.name} scored: ${res.data.score}/${res.data.max_score} - ${res.data.severity}`);
      onSaved(res.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <SlidePanel open={open} onClose={onClose} title="Administer Outcome Measure" width="max-w-4xl" testId="administer-panel">
      <div className="flex gap-4">
        <SidebarNav sections={sections} active={active} setActive={setActive} completed={completed} />
        <div className="flex-1 space-y-4 min-w-0">
          {active === "client" && (
            <div className="space-y-4" data-testid="om-client-section">
              <div className="grid grid-cols-2 gap-4">
                <div><FLabel>Client *</FLabel><ClientSelect clients={clients} value={clientId} onChange={setClientId} testId="om-client-select" /></div>
                <div><FLabel>Date</FLabel><DateField testId="om-date" value={date} onChange={setDate} noFuture /></div>
              </div>
              <div><FLabel>Instrument *</FLabel>
                <FSelect data-testid="om-instrument-select" value={instKey} onChange={(e) => setInstKey(e.target.value)}>
                  <option value="">Select an instrument…</option>
                  {instruments.map((i) => <option key={i.key} value={i.key}>{i.name} - {i.full_name}</option>)}
                </FSelect>
              </div>
              {inst && (
                <div className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 text-xs text-[#6D4C41]">
                  <div className="flex items-center gap-1 font-bold text-[#3E2723]"><Info size={12} /> {inst.name} - {inst.category}</div>
                  <div className="mt-1">{inst.questions.length} items · max score {inst.max_score}</div>
                  {isBehavioral && (
                    <div className="mt-1 flex items-center gap-1 text-[#C62828] font-semibold"><AlertTriangle size={11} /> Suicide Risk Screen is mandatory for this instrument.</div>
                  )}
                </div>
              )}
            </div>
          )}
          {active === "session" && <SessionInfoSection session={session} setSession={setSession} />}
          {active === "instrument" && <InstrumentSection inst={inst} answers={answers} setAnswers={setAnswers} notes={notes} setNotes={setNotes} />}
          {active === "srs" && <SrsSection srsMeta={srsMeta} srsAnswers={srsAnswers} setSrsAnswers={setSrsAnswers} mandatory={isBehavioral} />}
          {active === "program" && <ProgramNotificationSection prog={prog} setProg={setProg} />}

          <div className="flex items-center justify-between gap-2 border-t border-[#EFEBE0] pt-4">
            <div className="flex items-center gap-2">
              <Btn variant="ghost" data-testid="om-back-btn" disabled={sections.findIndex((s) => s.key === active) === 0}
                onClick={() => {
                  const idx = sections.findIndex((s) => s.key === active);
                  if (idx > 0) setActive(sections[idx - 1].key);
                }}>
                <ChevronLeft size={12} /> Back
              </Btn>
              <Btn variant="outline" data-testid="om-next-btn" disabled={sections.findIndex((s) => s.key === active) === sections.length - 1}
                onClick={() => {
                  const idx = sections.findIndex((s) => s.key === active);
                  if (idx < sections.length - 1) setActive(sections[idx + 1].key);
                }}>
                Next <ChevronRight size={12} />
              </Btn>
            </div>
            <div className="flex gap-2">
              <Btn variant="secondary" onClick={onClose} data-testid="om-cancel-btn">Cancel</Btn>
              <Btn onClick={save} disabled={busy} data-testid="om-score-save-btn">{busy ? "Scoring…" : "Score & Save"}</Btn>
            </div>
          </div>
        </div>
      </div>
    </SlidePanel>
  );
}

function TrendMini({ clientId, instrument }) {
  const [points, setPoints] = useState([]);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/outcome-measures/trends?client_id=${clientId}`)
      .then((r) => {
        const s = (r.data || []).find((x) => x.instrument === instrument);
        setPoints(s?.points || []);
      }).catch(() => {});
  }, [clientId, instrument]);
  if (!points.length) return null;
  return (
    <div className="h-40" data-testid="om-360-trend">
      <ResponsiveContainer>
        <LineChart data={points.map((p) => ({ ...p, label: dayjs(p.date).format("MM/DD") }))}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v, n, p) => [`${v} - ${p.payload.severity}`, "Score"]} />
          <Line type="monotone" dataKey="score" stroke="#4E342E" strokeWidth={2} dot={{ r: 4, fill: "#8D6E63" }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function AddendumForm({ rowId, onDone }) {
  const [reason, setReason] = useState("");
  const [text, setText] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const openSign = () => {
    if (!reason.trim()) return toast.error("Reason is required");
    if (!text.trim()) return toast.error("Addendum text is required");
    setSignOpen(true);
  };

  const onConfirmSign = async ({ image, name }) => {
    setBusy(true);
    try {
      await api.post(`/outcome-measures/${rowId}/addendum`, { reason, text, signature: name, signature_image: image });
      toast.success("Addendum appended and signed");
      setReason(""); setText("");
      setSignOpen(false);
      if (typeof onDone === "function") onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3" data-testid="om-addendum-form">
      <div className="text-sm font-bold text-[#3E2723]">Add Addendum</div>
      <div className="text-[10px] text-[#7B6157]">Existing content is preserved for HIPAA retention. Addenda are appended, timestamped, and e-signed with a full audit stamp.</div>
      <FInput data-testid="om-add-reason" placeholder="Reason (e.g., correction, clarification, late-added detail)" value={reason} onChange={(e) => setReason(e.target.value)} />
      <FTextarea data-testid="om-add-text" rows={3} placeholder="Addendum text" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="flex justify-end"><Btn data-testid="om-add-save" onClick={openSign} disabled={busy}><PenLine size={13} /> Sign and Append</Btn></div>
      <SignaturePromptModal open={signOpen} onClose={() => setSignOpen(false)} onConfirm={onConfirmSign}
        busy={busy} title="Sign Addendum"
        subtitle="Your signature is applied to this addendum with a HIPAA-compliant audit stamp." />
    </div>
  );
}

function ThreeSixtyPanel({ rowId, onClose, onArchived }) {
  const [full, setFull] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!rowId) return;
    api.get(`/outcome-measures/${rowId}`).then((r) => setFull(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(load, [rowId]);

  const archive = async () => {
    setBusy(true);
    try {
      await api.post(`/outcome-measures/${rowId}/archive`, { reason: archiveReason });
      toast.success("Record archived (retained for HIPAA)");
      setConfirmArchive(false);
      onArchived();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!rowId) return null;
  const row = full;
  return (
    <SlidePanel open={!!rowId} onClose={onClose}
      title={row ? `${row.instrument_name} - ${row.client_name}` : "Loading…"}
      subtitle={row ? `${dayjs(row.date).format("MMM D, YYYY")} · Administered by ${row.administered_by}${row.status === "archived" ? " · ARCHIVED" : ""}` : ""}
      width="max-w-3xl" testId="om-360-panel">
      {!row ? <Empty text="Loading 360 view…" /> : (
        <div className="space-y-4">
          <div className={`rounded-2xl p-5 ${RISK_TONE[row.risk_level] || RISK_TONE.low}`} data-testid="om-360-score">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-3xl font-bold">{row.score}<span className="text-lg font-medium">/{row.max_score}</span></div>
                <div className="text-sm font-semibold">{row.severity}</div>
              </div>
              {row.srs && (
                <div className={`rounded-xl px-3 py-2 text-xs ${RISK_TONE[row.srs.risk_level] || RISK_TONE.low}`} data-testid="om-360-srs-badge">
                  <div className="font-bold">C-SSRS Screen</div>
                  <div>{row.srs.score}/6 · {row.srs.severity}</div>
                </div>
              )}
            </div>
          </div>

          {row.flags?.length > 0 && (
            <div className="rounded-xl border border-[#E53935]/30 bg-[#FFEBEE] p-3" data-testid="om-360-flags">
              {row.flags.map((f, i) => <div key={i} className="flex items-start gap-2 text-sm font-semibold text-[#C62828]"><AlertTriangle size={15} className="mt-0.5 shrink-0" />{f}</div>)}
            </div>
          )}

          {row.bands && row.bands.length > 0 && (
            <div data-testid="om-360-scoring-table">
              <h4 className="mb-2 text-sm font-bold text-[#3E2723]">Scoring and interpretation</h4>
              <Table headers={["Score", "Zone", "Interpretation"]}>
                {row.bands.map((b, i) => {
                  const active = row.score >= b.min && row.score <= b.max;
                  return (
                    <tr key={i} className={active ? "bg-[#FFF8E1]" : ""} data-testid={`om-360-band-${i}`}>
                      <Td className="font-semibold">{b.min === b.max ? b.min : `${b.min}-${b.max}`}</Td>
                      <Td><Badge value={b.label.split(" - ")[0] || b.label} /></Td>
                      <Td className="text-[#5D4037]">{b.label}{active && <span className="ml-2 rounded bg-[#4E342E] px-1.5 py-0.5 text-[10px] font-bold text-white">CURRENT</span>}</Td>
                    </tr>
                  );
                })}
              </Table>
            </div>
          )}

          <div>
            <h4 className="mb-2 text-sm font-bold text-[#3E2723]">Score trend for this client on {row.instrument_name}</h4>
            <TrendMini clientId={row.client_id} instrument={row.instrument} />
          </div>

          {row.instrument_questions && row.instrument_questions.length > 0 && (
            <div data-testid="om-360-items">
              <h4 className="mb-2 text-sm font-bold text-[#3E2723]">Item responses</h4>
              <div className="space-y-1.5">
                {row.instrument_questions.map((q, i) => {
                  const opts = row.instrument_per_question_options?.[String(i)] || row.instrument_options || [];
                  const label = (opts.find(([v]) => Number(v) === row.answers[i]) || [])[1] || row.answers[i];
                  return (
                    <div key={i} className="flex items-start justify-between gap-3 rounded-lg border border-[#EFEBE0] px-3 py-2 text-xs">
                      <span className="text-[#5D4037]">{i + 1}. {q}</span>
                      <span className="shrink-0 font-semibold text-[#3E2723]">{label} ({row.answers[i]})</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {row.program_notification?.progress && (
            <div data-testid="om-360-program">
              <h4 className="mb-2 text-sm font-bold text-[#3E2723]">Program and Treatment Notification</h4>
              <div className="rounded-xl border border-[#EFEBE0] p-3 text-xs text-[#5D4037] space-y-1">
                <div><span className="font-semibold">Progress:</span> {PROGRESS_OPTIONS.find(([v]) => v === row.program_notification.progress)?.[1] || row.program_notification.progress}</div>
                <div><span className="font-semibold">Needs plan modification:</span> {row.program_notification.needs_modification ? "Yes" : "No"}</div>
                {row.program_notification.modifications && <div><span className="font-semibold">Modifications:</span> {row.program_notification.modifications}</div>}
                {row.program_notification.additional_info && <div><span className="font-semibold">Additional info:</span> {row.program_notification.additional_info}</div>}
              </div>
            </div>
          )}

          <div data-testid="om-360-audit">
            <h4 className="mb-2 text-sm font-bold text-[#3E2723]">Signature and audit</h4>
            <div className="rounded-xl border border-[#EFEBE0] p-3 text-xs text-[#5D4037]">
              Administered by <span className="font-semibold text-[#3E2723]">{row.administered_by}</span> on {dayjs(row.created_at).format("MMM D, YYYY [at] h:mm A")}.
              {row.updated_at && <> Last modified {dayjs(row.updated_at).format("MMM D, YYYY [at] h:mm A")}.</>}
              {row.status === "archived" && (
                <div className="mt-2 rounded-lg bg-[#FFEBEE] px-2 py-1 text-[11px] font-bold text-[#C62828]">
                  Archived {row.archived_at ? `on ${dayjs(row.archived_at).format("MMM D, YYYY")}` : ""} by {row.archived_by_name || "-"}{row.archive_reason ? ` - ${row.archive_reason}` : ""}. Retained per HIPAA policy.
                </div>
              )}
            </div>
          </div>

          <div data-testid="om-360-addenda">
            <h4 className="mb-2 text-sm font-bold text-[#3E2723]">Addenda ({(row.addenda || []).length})</h4>
            <div className="space-y-2">
              {(row.addenda || []).length === 0 && <div className="text-xs text-[#84675B]">No addenda have been appended.</div>}
              {(row.addenda || []).map((a) => (
                <div key={a.id} className="rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3 text-xs" data-testid={`om-360-addendum-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-[#3E2723]">{a.reason}</div>
                    <div className="text-[10px] text-[#7B6157]">{dayjs(a.created_at).format("MMM D, YYYY h:mm A")} by {a.author_name}</div>
                  </div>
                  <div className="mt-1 text-[#5D4037] whitespace-pre-wrap">{a.text}</div>
                  <div className="mt-1 text-[10px] italic text-[#7B6157]">Signed: {a.signature}</div>
                </div>
              ))}
            </div>
            {row.status !== "archived" && <div className="mt-3"><AddendumForm rowId={rowId} onDone={load} /></div>}
          </div>

          {row.status !== "archived" && (
            <div className="flex items-center justify-between rounded-xl border border-[#EFEBE0] bg-[#FDFCF8] p-3">
              <div className="text-xs text-[#6D4C41]">
                <div className="font-semibold text-[#3E2723]">HIPAA retention</div>
                <div>Records are archived, not deleted. Cocoa EHR default is 7 years post last-service.</div>
              </div>
              <Btn variant="outline" data-testid="om-360-archive" onClick={() => setConfirmArchive(true)} className="!text-[#C62828] !border-[#C62828]">
                <Archive size={12} /> Archive
              </Btn>
            </div>
          )}
        </div>
      )}

      <ConfirmDialog open={confirmArchive} onClose={() => setConfirmArchive(false)} onConfirm={archive}
        title="Archive record (HIPAA-retained)"
        description={`This record will be hidden from default lists but retained for the required 7-year HIPAA period. Optional reason: "${archiveReason || "not provided"}"`}
        confirmWord="ARCHIVE" confirmLabel="Archive" busy={busy}
        warning="Records are never destroyed. Archiving preserves the audit trail." />
      {confirmArchive && (
        <div className="fixed left-1/2 top-24 z-[85] w-full max-w-md -translate-x-1/2 rounded-xl bg-white p-3 shadow-2xl" data-testid="om-archive-reason-wrap">
          <FInput data-testid="om-archive-reason" placeholder="Reason (optional) - e.g., duplicate entry, wrong client"
            value={archiveReason} onChange={(e) => setArchiveReason(e.target.value)} />
          <div className="mt-1 text-[10px] text-[#7B6157]">This reason is written to the audit log.</div>
        </div>
      )}
    </SlidePanel>
  );
}

function TrendsSection({ clientId, clients }) {
  const [series, setSeries] = useState([]);
  useEffect(() => {
    if (!clientId) { setSeries([]); return; }
    api.get(`/outcome-measures/trends?client_id=${clientId}`).then((r) => setSeries(r.data)).catch(() => {});
  }, [clientId]);
  if (!clientId) return null;
  const client = clients.find((c) => c.id === clientId);
  if (!series.length) return <Card className="p-5 text-sm text-[#84675B]" data-testid="om-trends-empty">No administrations yet for {client?.first_name} {client?.last_name}.</Card>;
  return (
    <div className="grid gap-4 lg:grid-cols-2" data-testid="om-trends">
      {series.map((s) => (
        <Card key={s.instrument} className="p-5">
          <h3 className="mb-3 flex items-center gap-2 font-heading text-sm font-bold text-[#3E2723]"><TrendingUp size={15} /> {s.name} score trend</h3>
          <div className="h-52">
            <ResponsiveContainer>
              <LineChart data={s.points.map((p) => ({ ...p, label: dayjs(p.date).format("MM/DD") }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis domain={[0, s.max_score]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, n, p) => [`${v} - ${p.payload.severity}`, "Score"]} />
                <Line type="monotone" dataKey="score" stroke="#4E342E" strokeWidth={2} dot={{ r: 4, fill: "#8D6E63" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ))}
    </div>
  );
}

export default function OutcomeMeasures() {
  const [instruments, setInstruments] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterClient, setFilterClient] = useState("");
  const [filterInst, setFilterInst] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [viewRowId, setViewRowId] = useState(null);
  const [srsMeta, setSrsMeta] = useState(null);
  const [behavioralCategories, setBehavioralCategories] = useState([]);
  const [tab, setTab] = useState("administrations");
  const [rollup, setRollup] = useState(null);
  const [rollupDays, setRollupDays] = useState(180);
  const clients = useClients();
  const pg = usePagination(rows);

  const loadRollup = () => {
    api.get(`/outcome-measures/cohort-rollup?days=${rollupDays}`)
      .then((r) => setRollup(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => { if (tab === "rollup") loadRollup(); }, [tab, rollupDays]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    const p = new URLSearchParams();
    if (filterClient) p.set("client_id", filterClient);
    if (filterInst) p.set("instrument", filterInst);
    if (showArchived) p.set("include_archived", "true");
    api.get(`/outcome-measures?${p}`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { api.get("/outcome-measures/instruments").then((r) => setInstruments(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/outcome-measures/srs-questions")
      .then((r) => { setSrsMeta(r.data); setBehavioralCategories(r.data.behavioral_health_categories || []); })
      .catch(() => {});
  }, []);
  useEffect(load, [filterClient, filterInst, showArchived]);

  const catGroups = useMemo(() => {
    const g = {};
    instruments.forEach((i) => { (g[i.category] = g[i.category] || []).push(i); });
    return g;
  }, [instruments]);

  return (
    <div data-testid="outcome-measures-page">
      <PageHeader title="Clinical Outcome Measures"
        subtitle="Standardized instruments with automated scoring, mandatory Suicide Risk Screen for behavioral-health measures, addenda, and HIPAA-retained archives"
        action={<Btn onClick={() => setPanelOpen(true)} data-testid="administer-btn"><Plus size={15} /> Administer Measure</Btn>} />

      <div className="mb-4 flex gap-2" data-testid="om-tabs">
        {[["administrations", "Individual Administrations"], ["rollup", "Cohort Roll-Up"]].map(([k, l]) => (
          <button key={k} data-testid={`om-tab-${k}`} onClick={() => setTab(k)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === k ? "bg-[#4E342E] text-white" : "bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#EDE7DC]"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "rollup" && (
        <CohortRollupView rollup={rollup} rollupDays={rollupDays} setRollupDays={setRollupDays} />
      )}

      {tab === "administrations" && (
      <>
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="instrument-library">
        {Object.entries(catGroups).map(([cat, list]) => (
          <Card key={cat} className="p-4">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#7B6157]">{cat}</div>
            {list.map((i) => (
              <div key={i.key} className="flex items-center justify-between py-1.5">
                <div><span className="text-sm font-semibold text-[#3E2723]">{i.name}</span>
                  <span className="ml-2 text-xs text-[#84675B]">{i.questions.length} items · max {i.max_score}</span></div>
                {i.is_risk_screen && <span className="rounded-full bg-[#FFEBEE] px-2 py-0.5 text-[10px] font-bold text-[#C62828]">RISK SCREEN</span>}
              </div>
            ))}
          </Card>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-56"><FLabel>Filter by client</FLabel><ClientSelect clients={clients} value={filterClient} onChange={setFilterClient} testId="om-filter-client" /></div>
        <div className="w-56"><FLabel>Filter by instrument</FLabel>
          <FSelect data-testid="om-filter-instrument" value={filterInst} onChange={(e) => setFilterInst(e.target.value)} placeholder="All instruments">
            {instruments.map((i) => <option key={i.key} value={i.key}>{i.name}</option>)}
          </FSelect></div>
        <label className="flex items-center gap-1.5 text-xs text-[#4E342E]">
          <input type="checkbox" data-testid="om-show-archived" checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)} />
          <span>Show archived</span>
        </label>
      </div>

      <TrendsSection clientId={filterClient} clients={clients} />

      <Card className="mt-4 overflow-hidden" data-testid="om-administrations-table">
        {rows.length === 0 ? <Empty text="No administrations yet. Click 'Administer Measure' to begin." /> : (
          <Table headers={["Date", "Client", "Instrument", "Result", "Administered by", ""]}>
            {pg.pageRows.map((r) => {
              const archived = r.status === "archived";
              return (
                <tr key={r.id} data-testid={`om-row-${r.id}`}
                  onClick={() => setViewRowId(r.id)}
                  className={`cursor-pointer border-t border-[#F1EEE7] transition-colors hover:bg-[#FDFCF8] ${archived ? "opacity-60" : ""}`}
                  title="Click to open the 360 view">
                  <Td>{dayjs(r.date).format("MM/DD/YYYY")}</Td>
                  <Td className="font-medium">{r.client_name}</Td>
                  <Td>{r.instrument_name}{archived && <span className="ml-2 rounded bg-[#FFEBEE] px-1.5 py-0.5 text-[9px] font-bold text-[#C62828]">ARCHIVED</span>}</Td>
                  <Td><ScoreBadge row={r} /></Td>
                  <Td className="text-[#7B6157]">{r.administered_by}</Td>
                  <Td>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <button data-testid={`om-view-${r.id}`} onClick={() => setViewRowId(r.id)} className="rounded-lg p-1.5 text-[#6D4C41] hover:bg-[#F5F2EB]" aria-label="Open 360 view"><Eye size={15} /></button>
                    </div>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
        <PaginationBar {...pg} testId="om-pagination" />
      </Card>
      </>
      )}

      <AdministerPanel open={panelOpen} onClose={() => setPanelOpen(false)}
        instruments={instruments} clients={clients}
        onSaved={(row) => { setPanelOpen(false); load(); setViewRowId(row.id); }}
        srsMeta={srsMeta} behavioralCategories={behavioralCategories} />
      <ThreeSixtyPanel rowId={viewRowId} onClose={() => setViewRowId(null)}
        onArchived={() => { setViewRowId(null); load(); }} />
    </div>
  );
}

function CohortRollupView({ rollup, rollupDays, setRollupDays }) {
  if (!rollup) return <div className="py-16 text-center text-sm text-[#84675B]">Computing cohort roll-up…</div>;
  const hasData = (rollup.instruments || []).length > 0;
  return (
    <div className="space-y-4" data-testid="om-rollup">
      <div className="flex flex-wrap items-center gap-3">
        <FLabel>Window</FLabel>
        <FSelect className="w-40" data-testid="om-rollup-days" value={rollupDays}
          onChange={(e) => setRollupDays(parseInt(e.target.value, 10))}>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last 365 days</option>
        </FSelect>
        <div className="ml-auto text-[11px] text-[#7B6157]">
          {rollup.total_administrations} administrations · {rollup.total_unique_clients} clients · from {rollup.cutoff}
        </div>
      </div>
      {!hasData ? (
        <Empty text={`No outcome administrations in the last ${rollupDays} days.`} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {rollup.instruments.map((inst) => (
            <Card key={inst.instrument} className="p-4" data-testid={`om-rollup-${inst.instrument}`}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="font-heading text-sm font-bold text-[#3E2723]">{inst.name}</div>
                  <div className="text-[11px] text-[#7B6157]">{inst.administered} administrations · {inst.unique_clients} unique clients</div>
                </div>
                <div className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                  inst.direction === "improved" ? "bg-[#E8F5E9] text-[#2E7D32]" :
                  inst.direction === "worsened" ? "bg-[#FFEBEE] text-[#C62828]" : "bg-[#F5F2EB] text-[#6D4C41]"}`}>
                  {inst.direction === "improved" ? `Improved ${inst.mean_change}` : inst.direction === "worsened" ? `Worsened ${Math.abs(inst.mean_change)}` : "Steady"} pts
                </div>
              </div>
              <div className="mb-2 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-[#FDFCF8] p-2">
                  <div className="text-[10px] uppercase text-[#7B6157]">First mean</div>
                  <div className="font-heading text-lg font-bold text-[#3E2723]">{inst.mean_first}</div>
                </div>
                <div className="rounded-lg bg-[#FDFCF8] p-2">
                  <div className="text-[10px] uppercase text-[#7B6157]">Latest mean</div>
                  <div className="font-heading text-lg font-bold text-[#3E2723]">{inst.mean_latest}</div>
                </div>
                <div className="rounded-lg bg-[#FFEBEE] p-2">
                  <div className="text-[10px] uppercase text-[#C62828]">High risk now</div>
                  <div className="font-heading text-lg font-bold text-[#C62828]">{inst.high_risk_current}</div>
                </div>
              </div>
              {inst.trend_series && inst.trend_series.length > 1 && (
                <div className="h-40">
                  <ResponsiveContainer>
                    <LineChart data={inst.trend_series}>
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, inst.max_score || "auto"]} tick={{ fontSize: 10 }} />
                      <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE0" />
                      <Tooltip />
                      <Line type="monotone" dataKey="mean_score" stroke="#4E342E" strokeWidth={2} dot={{ r: 3, fill: "#4E342E" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]" data-testid={`om-rollup-severity-${inst.instrument}`}>
                {Object.entries(inst.latest_severity_distribution || {}).map(([sev, n]) => (
                  <span key={sev} className="rounded-full bg-[#F5F2EB] px-2 py-0.5 font-semibold text-[#6D4C41] capitalize">
                    {sev}: {n}
                  </span>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
